import { useState } from "react";
import {
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Typography,
  Chip,
  Box,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EventIcon from "@mui/icons-material/Event";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LinkIcon from "@mui/icons-material/Link";
import { StyledAccordion } from "./styled";
import { getBatchFullName, getStatusIcon, formatDate } from "./helpers";
import BatchStats from "./BatchStats";
import { useDispatch } from "react-redux";
import { linkSheetToBatch } from "../../../../../Reducer/productBatchesSlice";

const BatchItem = ({ batch, onUpload, onViewData }) => {
  const dispatch = useDispatch();
  const [sheetUrl, setSheetUrl] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [showLinkInput, setShowLinkInput] = useState(false);

  const handleLinkSheet = async () => {
    if (!sheetUrl.trim()) {
      setLinkError("Please paste a Google Sheet URL or ID");
      return;
    }
    setLinking(true);
    setLinkError("");
    try {
      await dispatch(linkSheetToBatch({ batchId: batch.id, sheetUrl: sheetUrl.trim() })).unwrap();
      setSheetUrl("");
      setShowLinkInput(false);
    } catch (err) {
      setLinkError(typeof err === "string" ? err : "Failed to link sheet");
    } finally {
      setLinking(false);
    }
  };

  return (
    <StyledAccordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel-${batch.id}-content`}
        id={`panel-${batch.id}-header`}
        sx={{
          padding: "16px",
          "& .MuiAccordionSummary-content": { alignItems: "center" },
        }}
      >
        <Avatar
          sx={{
            bgcolor: "rgba(102, 126, 234, 0.1)",
            color: "#667eea",
            mr: 2,
            width: 32,
            height: 32,
          }}
        >
          <EventIcon fontSize="small" />
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography fontWeight="600" variant="body1">
            {getBatchFullName(batch)}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {formatDate(batch.createdAt)}
          </Typography>
        </Box>
        <Chip
          icon={getStatusIcon(batch.deliveryStatus)}
          label={batch.deliveryStatus}
          size="small"
          color={
            batch.deliveryStatus === "delivered"
              ? "success"
              : batch.deliveryStatus === "processing"
                ? "warning"
                : "error"
          }
          variant="outlined"
          sx={{ ml: 1 }}
        />
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 1, background: "#fafafa" }}>
        <BatchStats batch={batch} />

        {/* Google Sheet Link Input */}
        {!batch.google_sheet_url && (
          <Box sx={{ mt: 1.5 }}>
            {!showLinkInput ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={() => setShowLinkInput(true)}
                sx={{
                  fontSize: "0.75rem",
                  textTransform: "none",
                  borderColor: "#4caf50",
                  color: "#4caf50",
                  "&:hover": { borderColor: "#388e3c", color: "#388e3c" },
                }}
              >
                Link Google Sheet
              </Button>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Paste Google Sheet URL or ID..."
                  value={sheetUrl}
                  onChange={(e) => { setSheetUrl(e.target.value); setLinkError(""); }}
                  error={!!linkError}
                  helperText={linkError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon fontSize="small" sx={{ color: "#4caf50" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.8rem" } }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleLinkSheet}
                    disabled={linking}
                    sx={{
                      background: "#4caf50",
                      fontSize: "0.75rem",
                      textTransform: "none",
                      "&:hover": { background: "#388e3c" },
                    }}
                    startIcon={linking ? <CircularProgress size={12} color="inherit" /> : <LinkIcon />}
                  >
                    {linking ? "Linking..." : "Save Link"}
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => { setShowLinkInput(false); setSheetUrl(""); setLinkError(""); }}
                    sx={{ fontSize: "0.75rem", textTransform: "none", color: "#999" }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 2,
            pt: 2,
            borderTop: "1px solid #eee",
          }}
        >
          <Button
            size="small"
            variant="contained"
            sx={{ background: "#1976d2" }}
            onClick={() => onUpload(batch.id)}
          >
            Upload CSV
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => onViewData(batch.id)}
          >
            View Data
          </Button>
        </Box>
      </AccordionDetails>
    </StyledAccordion>
  );
};

export default BatchItem;
